import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FollowUpSettingsDto, SaveFollowUpSettingsRequest } from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson, type ApiErrorBody } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';

const SETTINGS_PATH = '/api/fundraising/follow-up-settings';

/** The campaign's follow-up wording and donation link, for the Fundraiser's editor (Fundraising: Manage). */
export function useFollowUpSettings() {
  const { selectedCampaign } = useCampaignStore();
  return useQuery({
    queryKey: queryKeys.fundraising.followUpSettings(),
    queryFn: () => apiJson<FollowUpSettingsDto>(SETTINGS_PATH, { error: 'Failed to fetch the follow-up settings' }),
    enabled: authService.isAuthenticated() && !!selectedCampaign,
  });
}

/**
 * Save the link and all six slots. The response carries the bumped concurrency version, written
 * straight into the cache so the next save sends it. A 400 (unknown field) or 409 (changed elsewhere)
 * rejects with the server's message.
 */
export function useSaveFollowUpSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: SaveFollowUpSettingsRequest) =>
      apiJson<FollowUpSettingsDto>(SETTINGS_PATH, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        json: request,
        // A JSON `null` body reaches here as null, so keep the optional chain.
        error: (body: ApiErrorBody | null) => new Error(body?.error ?? body?.message ?? "Couldn't save the follow-ups. Try again."),
      }),
    onSuccess: (saved) => queryClient.setQueryData(queryKeys.fundraising.followUpSettings(), saved),
  });
}
